import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';

/* ───────────────────────────────────────────────────────────────
   Chat Input Registry + Keypress Intro Orchestrator

   Purpose: allow a single central keypress handler to animate
   typing into whichever page's chat input is currently visible,
   without prop-drilling or changing any rendering.

   Usage:
     1. Wrap app in <ChatInputRegistryProvider>
     2. Each page's chat input component calls:
          useRegisterChatInput(setMessage)
        — registers on mount, auto-unregisters on unmount.
     3. Keypress handler calls:
          const { playIntro, isIntroActive } = useChatIntro();
          playIntro(text, onComplete)
        — types char-by-char into whichever setter is currently
        registered. While typing, isIntroActive === true (use this
        as a lock to ignore more keypresses).
   ─────────────────────────────────────────────────────────────── */

const CHAR_INTERVAL_MS = 32;         // 30% faster than prior 45ms — closer to ChatGPT streaming speed
const POST_TYPE_PAUSE_MS = 150;      // short handoff pause so the send feels instant

const ChatInputRegistryContext = createContext(null);

export const ChatInputRegistryProvider = ({ children }) => {
  // Only the most-recently-registered setter is active (pages unmount others)
  const setterRef = useRef(null);
  const timerRef = useRef(null);
  const [isIntroActive, setIsIntroActive] = useState(false);

  const registerSetter = useCallback((setter) => {
    setterRef.current = setter;
    return () => {
      if (setterRef.current === setter) setterRef.current = null;
    };
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const playIntro = useCallback((text, onComplete) => {
    if (!text || !setterRef.current) {
      // No registered input — skip animation, invoke callback immediately
      onComplete?.();
      return;
    }
    clearTimer();
    setIsIntroActive(true);
    const setter = setterRef.current;
    setter('');
    let i = 0;

    const typeNextChar = () => {
      i += 1;
      if (setterRef.current === setter) {
        // Only keep typing if the same page is still mounted
        setter(text.slice(0, i));
      } else {
        // Page swapped during animation — abort cleanly
        setIsIntroActive(false);
        onComplete?.();
        return;
      }
      if (i < text.length) {
        timerRef.current = setTimeout(typeNextChar, CHAR_INTERVAL_MS);
      } else {
        // Done typing — breathe, then clear input and invoke callback
        timerRef.current = setTimeout(() => {
          if (setterRef.current === setter) setter('');
          setIsIntroActive(false);
          onComplete?.();
        }, POST_TYPE_PAUSE_MS);
      }
    };

    timerRef.current = setTimeout(typeNextChar, CHAR_INTERVAL_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => clearTimer, []);

  const value = { registerSetter, playIntro, isIntroActive };

  return (
    <ChatInputRegistryContext.Provider value={value}>
      {children}
    </ChatInputRegistryContext.Provider>
  );
};

/* Hook for pages — register their setMessage as the current typing target */
export const useRegisterChatInput = (setter) => {
  const ctx = useContext(ChatInputRegistryContext);
  useEffect(() => {
    if (!ctx || !setter) return;
    return ctx.registerSetter(setter);
  }, [ctx, setter]);
};

/* Hook for orchestrators — trigger typing + check lock */
export const useChatIntro = () => {
  const ctx = useContext(ChatInputRegistryContext);
  return {
    playIntro: ctx?.playIntro || (() => {}),
    isIntroActive: ctx?.isIntroActive || false,
  };
};
